import React, { useCallback, useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Form from '@atlaskit/form';
import { Box, Stack, Text, xcss, Flex } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';

import type { AvailableSite } from '@atlassian/gic-anywhere';

import { fg } from '@confluence/feature-gating';

import { messages } from '../../messages';
import { saveJiraConfigDetailsToLocalStorage } from '../../utils/localStorageHelpers';
import { SiteSelector } from '../work-create-form-common/SiteSelector';
import { useGetInitialDataForSingleIssueCreateForm } from '../../services/useGetInitialDataForSingleIssueCreateForm';
import { useIssueCreateSidePanelActions } from '../../providers/IssueCreateSidePanelContextProvider';

import { SingleWorkCreateFormFooter } from './SingleWorkCreateFormFooter';

const singleWorkCreateFormStyles = xcss({
	flexGrow: 1,
	overflowY: 'auto',
});

const singleWorkCreateFormContainerStyles = xcss({
	height: '100%',
});

// This is the view for both AI and non-AI single work create where users can edit and create individual issues
export const SingleWorkCreateForm = ({ closePanel = () => {} }: { closePanel?: () => void }) => {
	const { setCurrentSiteSelection } = useIssueCreateSidePanelActions();

	const {
		availableSites,
		isLoading: isSitesLoading,
		error,
	} = useGetInitialDataForSingleIssueCreateForm();
	const [selectedAvailableSite, setSelectedAvailableSite] = useState<AvailableSite | null>(null);

	const { formatMessage } = useIntl();

	// Optionally, auto-select the first site when availableSites changes
	useEffect(() => {
		if (availableSites.length > 0 && !selectedAvailableSite) {
			setSelectedAvailableSite(availableSites[0]);
			void setCurrentSiteSelection(availableSites[0].cloudId);
		}
	}, [availableSites, selectedAvailableSite, setCurrentSiteSelection]);

	const onSubmit = useCallback(() => {
		if (selectedAvailableSite) {
			void setCurrentSiteSelection(selectedAvailableSite.cloudId);
		}
		saveJiraConfigDetailsToLocalStorage({
			selectedSite: selectedAvailableSite ?? undefined,
		});
		// onSubmit
	}, [selectedAvailableSite, setCurrentSiteSelection]);

	const onClose = useCallback(() => {
		// TODO: do something onClose
	}, []);

	const isSaveDisabled = isSitesLoading || !selectedAvailableSite;
	const singleWorkCreateFormBodyText = fg('confluence-issue-terminology-refresh')
		? messages.singleWorkCreateFormBodyTextIssueTermRefresh
		: messages.singleWorkCreateFormBodyText;
	return (
		<Flex
			testId="single-work-create-form-wrapper"
			direction="column"
			justifyContent="space-between"
			alignItems="center"
			xcss={singleWorkCreateFormContainerStyles}
		>
			<Box padding="space.200" xcss={singleWorkCreateFormStyles}>
				<Stack space="space.100">
					<Text color="color.text.subtlest">
						<FormattedMessage {...singleWorkCreateFormBodyText} />
					</Text>
					<Text size="small" color="color.text.accent.gray.bolder">
						Required fields are marked with an asterisk *
					</Text>
					{error && (
						<SectionMessage appearance="error" testId="singleWorkCreateFormErrorSectionMessage">
							{error ? (
								error.message
							) : (
								<FormattedMessage {...messages.singleWorkCreateFormJiraErrorText} />
							)}
						</SectionMessage>
					)}
					<Stack space="space.300">
						<Form onSubmit={onSubmit}>
							{({ formProps }) => (
								<form
									id="single-work-create-form"
									data-testid="single-work-create-form"
									{...formProps}
								>
									<SiteSelector
										options={availableSites}
										value={selectedAvailableSite ?? undefined}
										onChange={(site) => site && setSelectedAvailableSite(site)}
										isLoading={isSitesLoading}
										isDisabled={isSitesLoading}
										isRequired
										label={formatMessage(messages.singleWorkCreateFormSite)}
									/>
								</form>
							)}
						</Form>
					</Stack>
				</Stack>
			</Box>
			<SingleWorkCreateFormFooter
				onClose={onClose}
				closePanel={closePanel}
				isSaveDisabled={isSaveDisabled}
			/>
		</Flex>
	);
};
